import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import requestAdminService from '../../services/requestAdminService';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const RequestAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', categoryId: '', fields: [] });

  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '' });

  const load = async () => {
    try {
      setLoading(true);
      const [t, c, s] = await Promise.all([
        requestAdminService.listTemplates(),
        requestAdminService.listCategories(),
        requestAdminService.getStatistics(),
      ]);
      setTemplates(t.data?.templates || t.data || []);
      setCategories(c.data?.categories || c.data || []);
      setStats(s.data || s);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load request system admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await requestAdminService.updateTemplate(editingTemplate.id, templateForm);
        toast.success('Template updated');
      } else {
        await requestAdminService.createTemplate(templateForm);
        toast.success('Template created');
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', categoryId: '', fields: [] });
      load();
    } catch (e) {
      console.error(e);
      toast.error('Saving template failed');
    }
  };

  const deleteTemplate = async (t) => {
    if (!confirm('Delete template?')) return;
    try {
      await requestAdminService.deleteTemplate(t.id);
      toast.success('Template deleted');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await requestAdminService.updateCategory(editingCategory.id, categoryForm);
        toast.success('Category updated');
      } else {
        await requestAdminService.createCategory(categoryForm);
        toast.success('Category created');
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '' });
      load();
    } catch (e) {
      console.error(e);
      toast.error('Saving category failed');
    }
  };

  const deleteCategory = async (c) => {
    if (!confirm('Delete category?')) return;
    try {
      await requestAdminService.deleteCategory(c.id);
      toast.success('Category deleted');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Request System Admin</h1>
          <p className="text-gray-600">Manage request templates, categories and view statistics</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"><div className="text-sm text-gray-500">Total Requests</div><div className="text-2xl font-semibold">{stats.totalRequests || 0}</div></div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"><div className="text-sm text-gray-500">Pending</div><div className="text-2xl font-semibold">{stats.pending || 0}</div></div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"><div className="text-sm text-gray-500">Completed</div><div className="text-2xl font-semibold">{stats.completed || 0}</div></div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Templates */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-semibold">Templates</div>
              <button className="px-3 py-2 bg-blue-600 text-white rounded flex items-center" onClick={() => { setShowTemplateForm(true); setEditingTemplate(null); }}>
                <PlusIcon className="w-5 h-5 mr-1" /> New
              </button>
            </div>
            <ul className="divide-y divide-gray-200">
              {templates.map((t) => (
                <li key={t.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-sm text-gray-500">Category: {t.category?.name || t.categoryId}</div>
                  </div>
                  <div className="space-x-2">
                    <button className="text-indigo-600" onClick={() => { setEditingTemplate(t); setTemplateForm({ name: t.name, categoryId: t.categoryId, fields: t.fields || [] }); setShowTemplateForm(true); }}><PencilIcon className="w-4 h-4 inline" /></button>
                    <button className="text-red-600" onClick={() => deleteTemplate(t)}><TrashIcon className="w-4 h-4 inline" /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-semibold">Categories</div>
              <button className="px-3 py-2 bg-blue-600 text-white rounded flex items-center" onClick={() => { setShowCategoryForm(true); setEditingCategory(null); }}>
                <PlusIcon className="w-5 h-5 mr-1" /> New
              </button>
            </div>
            <ul className="divide-y divide-gray-200">
              {categories.map((c) => (
                <li key={c.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="font-medium">{c.name}</div>
                  <div className="space-x-2">
                    <button className="text-indigo-600" onClick={() => { setEditingCategory(c); setCategoryForm({ name: c.name }); setShowCategoryForm(true); }}><PencilIcon className="w-4 h-4 inline" /></button>
                    <button className="text-red-600" onClick={() => deleteCategory(c)}><TrashIcon className="w-4 h-4 inline" /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Template Modal */}
        {showTemplateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-lg">
              <h2 className="text-lg font-semibold mb-4">{editingTemplate ? 'Edit Template' : 'New Template'}</h2>
              <form onSubmit={saveTemplate} className="space-y-3">
                <input className="border rounded px-3 py-2 w-full" placeholder="Name" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
                <input className="border rounded px-3 py-2 w-full" placeholder="Category ID" value={templateForm.categoryId} onChange={(e) => setTemplateForm({ ...templateForm, categoryId: e.target.value })} />
                <textarea className="border rounded px-3 py-2 w-full" rows={6} placeholder='Fields JSON (e.g., [{"name":"reason","type":"textarea","label":"Reason","required":true}])' value={JSON.stringify(templateForm.fields)} onChange={(e) => {
                  try { const val = e.target.value?.trim(); setTemplateForm({ ...templateForm, fields: val ? JSON.parse(val) : [] }); } catch { /* ignore */ }
                }} />
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowTemplateForm(false)}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryForm && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">{editingCategory ? 'Edit Category' : 'New Category'}</h2>
              <form onSubmit={saveCategory} className="space-y-3">
                <input className="border rounded px-3 py-2 w-full" placeholder="Name" value={categoryForm.name} onChange={(e) => setCategoryForm({ name: e.target.value })} />
                <div className="flex justify-end space-x-2 pt-2">
                  <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowCategoryForm(false)}>Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestAdmin;



